import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    getReelsSearchPreview,
    searchReels,
    getReelsComment,
    getAllReels,
    getFriendsReels,
    saveReels,
    unsaveReels,
    getSavedReels,
    getMyReels,
    getUserReels,
    deleteSingleReel,
    deleteAllUserReels
} from "./reelsAction";
import apiInstance from "../../config/api";
import { toast } from "react-toastify";
import { updateReelsCount } from "../user/userReducer";

export const uploadReels = createAsyncThunk('reels/upload', async ({ file, caption, type, user }, { dispatch }) => {
    const uploadId = `reels-uploads-${Date.now()}-${Math.random()}`
    try {
        const formData = new FormData()

        formData.append('video', file)
        formData.append('caption', caption)
        formData.append('type', type)

        const previewData = { caption, video: URL.createObjectURL(file), id: uploadId }
        dispatch(addUpload(previewData))

        const request = await apiInstance.post('/file/api/reels', formData, {
            onUploadProgress: (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                dispatch(setUploadProgress({ id: uploadId, progress: percent }));
            }
        })

        const response = request.data
        if (response.status === 'success') {
            dispatch(finishUpload({ id: uploadId }))
            toast.success("Reels uploaded successfully!", {
                position: 'bottom-left'
            });
            dispatch(updateReelsCount({ actionType: "add" }))
            return { ...response.post, user }
        } else {
            toast.warning(`Reels upload failed ${response.message}`);
        }
    }
    catch (error) {
        dispatch(finishUpload({ id: uploadId }))
        toast.warning(`Reels upload failed ${error.message}`);
    }
});

const initialState = {
    uploads: [],

    uploading: false,
    uploadStatus: 'idle',

    reelsPreviewLoading: false,
    reelsSearchPreview: [],

    searchingReels: false,
    searchedReels: [],

    loadingReels: false,
    currentReels: 'forYou',
    forYouReels: [],
    friendsReels: [],

    loadingSavedReels: false,
    savedReels: [],
    savedReelsError: null,

    reels: [],
    viewReels: [],

    userReelsLoading: false,
    userReels: [],
    userReelsError: null,

    myReelsLoading: false,
    myReels: [],
    myReelsError: null,

    commentLoading: false,

    // Delete states
    deletingReel: false,
    deletingAllReels: false,
    deleteError: null,

    error: null
}

const reelsSlice = createSlice({
    name: 'reels',
    initialState,
    reducers: {
        setCurrentReels: (state, action) => {
            if (action.payload === 'forYou') {
                state.currentReels = 'forYou'
                state.reels = state.forYouReels
            }
            else if (action.payload === 'friends') {
                state.currentReels = 'friends'
                state.reels = state.friendsReels
            }
        },

        setViewReels: (state, action) => {
            state.viewReels = action.payload
        },

        likeReelsAction: (state, action) => {
            const { type, index, postId } = action.payload
            if (type === "like") {
                state.viewReels[index].likesCount += 1;
                state.viewReels[index].likedByCurrentUser = true;
            } else if (type === "unlike") {
                state.viewReels[index].likesCount = Math.max(0, state.viewReels[index].likesCount - 1);
                state.viewReels[index].likedByCurrentUser = false;
            }

            const previewIndex = state.reelsSearchPreview.findIndex(reel => reel.postId === postId)
            if (previewIndex !== -1) {
                if (type === "like") {
                    state.reelsSearchPreview[previewIndex].likesCount += 1;
                    state.reelsSearchPreview[previewIndex].likedByCurrentUser = true;
                } else if (type === "unlike") {
                    state.reelsSearchPreview[previewIndex].likesCount = Math.max(0, state.reelsSearchPreview[previewIndex].likesCount - 1);
                    state.reelsSearchPreview[previewIndex].likedByCurrentUser = false;
                }
            }

        },
        commentReelAction: (state, action) => {
            const { type, index, postId } = action.payload
            if (type === 'add') {
                state.viewReels[index].commentsCount += 1;
            } else if (type === "remove") {
                state.viewReels[index].commentsCount = Math.max(0, state.viewReels[index].commentsCount - 1);
            }

            const previewIndex = state.reelsSearchPreview.findIndex(reel => reel.postId === postId)
            if (previewIndex !== -1) {
                if (type === "add") {
                    state.reelsSearchPreview[previewIndex].commentsCount += 1;
                } else if (type === "remove") {
                    state.reelsSearchPreview[previewIndex].commentsCount = Math.max(0, state.reelsSearchPreview[previewIndex].commentsCount - 1);
                }
            }
        },

        updateViewReels: (state, action) => {
            state.viewReels.push(...action.payload)
        },

        resetUploadStatus: (state) => {
            state.uploadStatus = initialState.uploadStatus
        },

        addUpload(state, action) {
            const { caption, video, id } = action.payload
            state.uploads.push({
                id: id,
                video: video,
                caption: caption,
                progress: 0,
                uploading: true,
            });
        },
        setUploadProgress(state, action) {
            const upload = state.uploads.find((u) => u.id === action.payload.id);
            if (upload) {
                upload.progress = action.payload.progress;
            }
        },
        finishUpload(state, action) {
            state.uploads = state.uploads.filter((u) => u.id !== action.payload.id);
        },

        // Delete reducers
        removeReelFromState: (state, action) => {
            const postId = action.payload;

            // Remove from myReels
            state.myReels = state.myReels.filter(reel => reel.postId !== postId);

            // Remove from userReels
            state.userReels = state.userReels.filter(reel => reel.postId !== postId);

            // Remove from viewReels
            state.viewReels = state.viewReels.filter(reel => reel.postId !== postId);

            // Remove from forYouReels
            state.forYouReels = state.forYouReels.filter(reel => reel.postId !== postId);

            // Remove from friendsReels
            state.friendsReels = state.friendsReels.filter(reel => reel.postId !== postId);

            // Remove from reels
            state.reels = state.reels.filter(reel => reel.postId !== postId);

            // Remove from reelsSearchPreview
            state.reelsSearchPreview = state.reelsSearchPreview.filter(reel => reel.postId !== postId);

            // Remove from savedReels
            state.savedReels = state.savedReels.filter(reel => reel.postId !== postId);
        },

        removeAllUserReelsFromState: (state) => {
            // Clear all reels arrays for the current user
            // Note: We need to know which reels belong to current user
            // This will be handled in the extraReducers when we have the deleted posts data
            state.myReels = [];
            state.userReels = state.userReels.filter(reel => {
                // Keep reels that don't belong to current user
                // This logic will be refined in the deleteAll extraReducer
                return true; // Placeholder, actual filtering done in extraReducer
            });
        },

        clearDeleteError: (state) => {
            state.deleteError = null;
        }
    },
    extraReducers: (builder) => {

        builder
            .addCase(uploadReels.pending, (state) => {
                state.uploading = true
            })
            .addCase(uploadReels.fulfilled, (state, action) => {
                state.uploading = false
                state.uploadStatus = 'success'
                if (action.payload) {
                    state.reelsSearchPreview.unshift(action.payload)
                    state.myReels.unshift(action.payload)
                    state.forYouReels.unshift(action.payload)
                }
            })
            .addCase(uploadReels.rejected, (state, action) => {
                state.uploading = false
                state.uploadStatus = 'rejected'
                state.error = action.error.message
            })

        builder
            .addCase(getAllReels.pending, (state) => {
                state.loadingReels = true
            })
            .addCase(getAllReels.fulfilled, (state, action) => {
                state.loadingReels = false
                state.viewReels.push(...action.payload)
                state.forYouReels = action.payload
                if (state.currentReels === 'forYou' && state.forYouReels.length > 0) {
                    state.reels = state.forYouReels
                }
            })
            .addCase(getAllReels.rejected, (state, action) => {
                state.loadingReels = false
                state.error = action.error.message
            })

        builder
            .addCase(getMyReels.pending, (state) => {
                state.myReelsLoading = true
            })
            .addCase(getMyReels.fulfilled, (state, action) => {
                state.myReelsLoading = false
                state.myReels = action.payload || []
            })
            .addCase(getMyReels.rejected, (state, action) => {
                state.myReelsLoading = false
                state.myReelsError = action.error.message
            })

        builder
            .addCase(getUserReels.pending, (state) => {
                state.userReelsLoading = true
                state.userReels = []
            })
            .addCase(getUserReels.fulfilled, (state, action) => {
                state.userReelsLoading = false
                state.userReels = action.payload || []
            })
            .addCase(getUserReels.rejected, (state, action) => {
                state.userReelsLoading = false
                state.userReelsError = action.error.message
            })

        builder
            .addCase(getFriendsReels.pending, (state) => {
                state.loadingReels = true
            })
            .addCase(getFriendsReels.fulfilled, (state, action) => {
                state.loadingReels = false
                state.friendsReels = action.payload || []
                if (state.currentReels === 'friends' && state.friendsReels.length > 0) {
                    console.log('this was executed')
                    state.reels = state.friendsReels
                }
            })
            .addCase(getFriendsReels.rejected, (state, action) => {
                state.loadingReels = false
                state.error = action.error.message
            })

        builder
            .addCase(getReelsSearchPreview.pending, (state) => {
                state.reelsPreviewLoading = true
            })
            .addCase(getReelsSearchPreview.fulfilled, (state, action) => {
                state.reelsPreviewLoading = false
                state.reelsSearchPreview = action.payload || []
            })
            .addCase(getReelsSearchPreview.rejected, (state, action) => {
                state.reelsPreviewLoading = false
                state.error = action.error.message
            })

        builder
            .addCase(searchReels.pending, (state) => {
                state.searchingReels = true
            })
            .addCase(searchReels.fulfilled, (state, action) => {
                state.searchingReels = false
                state.searchedReels = action.payload || []
            })
            .addCase(searchReels.rejected, (state, action) => {
                state.searchingReels = false
                state.error = action.error.message
            })

        builder
            .addCase(getReelsComment.pending, (state) => {
                state.commentLoading = true
            })
            .addCase(getReelsComment.fulfilled, (state) => {
                state.commentLoading = false
            })
            .addCase(getReelsComment.rejected, (state, action) => {
                state.commentLoading = false
                state.error = action.error.message
            })

        builder
            .addCase(saveReels.fulfilled, (state, action) => {
                const {
                    // message,
                    reel
                } = action.payload
                const postId = reel.postId
                const index = state.viewReels.findIndex(post => post.postId === postId)
                const previewIndex = state.reelsSearchPreview.findIndex(post => post.postId === postId)
                const myReelsIndex = state.myReels.findIndex(post => post.postId === postId)

                if (index !== -1) {
                    state.viewReels[index] = {
                        ...state.viewReels[index],
                        savedByCurrentUser: true,
                        saveCount: (state.viewReels[index].saveCount || 0) + 1
                    }
                }
                if (previewIndex !== -1) {
                    state.reelsSearchPreview[previewIndex] = {
                        ...state.reelsSearchPreview[previewIndex],
                        savedByCurrentUser: true,
                        saveCount: (state.reelsSearchPreview[previewIndex].saveCount || 0) + 1
                    }
                }
                if (myReelsIndex !== -1) {
                    state.myReels[myReelsIndex] = {
                        ...state.myReels[myReelsIndex],
                        savedByCurrentUser: true,
                        saveCount: (state.myReels[myReelsIndex].saveCount || 0) + 1
                    }
                }

                state.savedReels.unshift(reel)
            })

            .addCase(unsaveReels.fulfilled, (state, action) => {
                const reelId = action.payload
                const index = state.viewReels.findIndex(post => post.postId === reelId)
                const previewIndex = state.reelsSearchPreview.findIndex(post => post.postId === reelId)
                const myReelsIndex = state.myReels.findIndex(post => post.postId === reelId)

                if (index !== -1) {
                    state.viewReels[index] = {
                        ...state.viewReels[index],
                        savedByCurrentUser: false,
                        saveCount: Math.max(0, (state.viewReels[index].saveCount || 0) - 1)
                    }
                }

                if (previewIndex !== -1) {
                    state.reelsSearchPreview[previewIndex] = {
                        ...state.reelsSearchPreview[previewIndex],
                        savedByCurrentUser: false,
                        saveCount: Math.max(0, (state.reelsSearchPreview[previewIndex].saveCount || 0) - 1)
                    }
                }

                if (myReelsIndex !== -1) {
                    state.myReels[myReelsIndex] = {
                        ...state.myReels[myReelsIndex],
                        savedByCurrentUser: false,
                        saveCount: Math.max(0, (state.myReels[myReelsIndex].saveCount || 0) - 1)
                    }
                }

                const filteredSavedPost = state.savedReels.filter(post => post.postId !== reelId)
                state.savedReels = filteredSavedPost
            })

        builder
            .addCase(getSavedReels.pending, (state) => {
                state.loadingSavedReels = true
            })
            .addCase(getSavedReels.fulfilled, (state, action) => {
                state.loadingSavedReels = false
                state.savedReels = action.payload || []
            })
            .addCase(getSavedReels.rejected, (state, action) => {
                state.loadingSavedReels = false
                state.savedReelsError = action.error.message
            })

        // ============== DELETE SINGLE REEL CASES ==============
        builder
            .addCase(deleteSingleReel.pending, (state) => {
                state.deletingReel = true
                state.deleteError = null
            })
            .addCase(deleteSingleReel.fulfilled, (state, action) => {
                state.deletingReel = false
                const { postId, reel } = action.payload

                // Remove reel from all state arrays
                state.myReels = state.myReels.filter(reel => reel.postId !== postId)
                state.userReels = state.userReels.filter(reel => reel.postId !== postId)
                state.viewReels = state.viewReels.filter(reel => reel.postId !== postId)
                state.forYouReels = state.forYouReels.filter(reel => reel.postId !== postId)
                state.friendsReels = state.friendsReels.filter(reel => reel.postId !== postId)
                state.reels = state.reels.filter(reel => reel.postId !== postId)
                state.reelsSearchPreview = state.reelsSearchPreview.filter(reel => reel.postId !== postId)
                state.savedReels = state.savedReels.filter(reel => reel.postId !== postId)
            })
            .addCase(deleteSingleReel.rejected, (state, action) => {
                state.deletingReel = false
                state.deleteError = action.payload || action.error.message
            })

        // ============== DELETE ALL USER REELS CASES ==============
        builder
            .addCase(deleteAllUserReels.pending, (state) => {
                state.deletingAllReels = true
                state.deleteError = null
            })
            .addCase(deleteAllUserReels.fulfilled, (state, action) => {
                state.deletingAllReels = false
                const { posts } = action.payload

                if (posts && posts.length > 0) {
                    const deletedPostIds = posts.map(post => post.postId)

                    // Remove all user's reels from all state arrays
                    state.myReels = []
                    state.userReels = state.userReels.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.viewReels = state.viewReels.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.forYouReels = state.forYouReels.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.friendsReels = state.friendsReels.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.reels = state.reels.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.reelsSearchPreview = state.reelsSearchPreview.filter(reel => !deletedPostIds.includes(reel.postId))
                    state.savedReels = state.savedReels.filter(reel => !deletedPostIds.includes(reel.postId))
                }

                // toast.success("All your reels have been deleted");
            })
            .addCase(deleteAllUserReels.rejected, (state, action) => {
                state.deletingAllReels = false
                state.deleteError = action.payload || action.error.message
            })
    }
})

export const {
    resetUploadStatus,
    setCurrentReels,
    setViewReels,
    updateViewReels,
    addUpload,
    setUploadProgress,
    finishUpload,
    likeReelsAction,
    commentReelAction,
    removeReelFromState,
    removeAllUserReelsFromState,
    clearDeleteError
} = reelsSlice.actions

export default reelsSlice.reducer;